import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance with interceptors
const apiClient = axios.create();

// Track if session expired modal is already showing
let sessionExpiredShowing = false;
let sessionTimer = null;

const showSessionExpiredModal = () => {
  if (sessionExpiredShowing) return;
  if (window.location.pathname === '/') return;
  sessionExpiredShowing = true;

  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;';

  const modal = document.createElement('div');
  modal.style.cssText = 'background:#fff;border-radius:8px;padding:32px 40px;text-align:center;box-shadow:0 4px 24px rgba(0,0,0,0.2);max-width:360px;width:90%;';

  const title = document.createElement('h3');
  title.textContent = 'Session Expired';
  title.style.cssText = 'margin:0 0 12px;font-size:18px;font-weight:600;color:#333;';

  const msg = document.createElement('p');
  msg.textContent = 'Your session has expired. Please log in again.';
  msg.style.cssText = 'margin:0 0 24px;font-size:14px;color:#666;';

  const btn = document.createElement('button');
  btn.textContent = 'OK';
  btn.style.cssText = 'background:#4ECFDB;color:#fff;border:none;padding:10px 40px;border-radius:4px;font-size:14px;cursor:pointer;font-weight:500;';
  btn.onmouseover = () => btn.style.background = '#3AB8C5';
  btn.onmouseout = () => btn.style.background = '#4ECFDB';
  btn.onclick = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionExpiredShowing = false;
    window.location.href = '/';
  };

  modal.appendChild(title);
  modal.appendChild(msg);
  modal.appendChild(btn);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
};

// Start a timer that shows the modal when the JWT expires
export const startSessionTimer = (token) => {
  if (sessionTimer) clearTimeout(sessionTimer);
  if (!token) return;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // convert to ms
    const msUntilExpiry = exp - Date.now();
    if (msUntilExpiry <= 0) {
      showSessionExpiredModal();
      return;
    }
    sessionTimer = setTimeout(() => showSessionExpiredModal(), msUntilExpiry);
  } catch { /* invalid token, ignore */ }
};

export const clearSessionTimer = () => {
  if (sessionTimer) {
    clearTimeout(sessionTimer);
    sessionTimer = null;
  }
  sessionExpiredShowing = false;
};

apiClient.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config;
    // Retry once on network errors or 5xx
    if (!config._retried && (!error.response || error.response.status >= 500)) {
      config._retried = true;
      await new Promise(r => setTimeout(r, 1000));
      return apiClient(config);
    }
    if (error.response && error.response.status === 401 && window.location.pathname !== '/') {
      showSessionExpiredModal();
    }
    return Promise.reject(error);
  }
);

export const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
  // Force browser/CDN to revalidate every request — no stale board data after edits.
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  'Pragma': 'no-cache'
});

// Projects API
export const projectsAPI = {
  getAll: async (status = 'open') => {
    // Append _t to bust any browser/CDN cache so boards always show fresh data after edits.
    const response = await apiClient.get(`${API_URL}/projects?status=${status}&_t=${Date.now()}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  getByCustomer: async (customerId) => {
    const response = await apiClient.get(`${API_URL}/projects?customer_id=${customerId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  getOne: async (id) => {
    const response = await apiClient.get(`${API_URL}/projects/${id}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getNotes: async (id) => {
    const response = await apiClient.get(`${API_URL}/projects/${id}/notes?_t=${Date.now()}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
  
  create: async (data) => {
    const response = await apiClient.post(`${API_URL}/projects`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await apiClient.put(`${API_URL}/projects/${id}`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  addNote: async (id, note) => {
    const response = await apiClient.post(`${API_URL}/projects/${id}/notes`, note, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`${API_URL}/projects/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  }
};

// Customers API
export const customersAPI = {
  getAll: async () => {
    const response = await apiClient.get(`${API_URL}/customers`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  getOne: async (id) => {
    const response = await apiClient.get(`${API_URL}/customers/${id}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  checkDuplicate: async (lastName) => {
    const response = await apiClient.get(`${API_URL}/customers/check-duplicate`, {
      headers: getAuthHeaders(),
      params: { last_name: lastName },
    });
    return response.data;
  },
  
  create: async (data) => {
    const response = await apiClient.post(`${API_URL}/customers`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await apiClient.put(`${API_URL}/customers/${id}`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`${API_URL}/customers/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  }
};

// Withdrawals API
export const withdrawalsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await apiClient.get(`${API_URL}/withdrawals?${params}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  create: async (data) => {
    const response = await apiClient.post(`${API_URL}/withdrawals`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`${API_URL}/withdrawals/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  update: async (id, data) => {
    const response = await apiClient.put(`${API_URL}/withdrawals/${id}`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  }
};

// Locations API
export const locationsAPI = {
  getAll: async () => {
    const response = await apiClient.get(`${API_URL}/locations`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await apiClient.put(`${API_URL}/locations/${id}`, data, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  confirmSelection: async () => {
    const response = await apiClient.post(`${API_URL}/locations/confirm`, {}, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
};

// Timeslips API
export const timeslipsAPI = {
  delete: async (slipId) => {
    const response = await apiClient.delete(`${API_URL}/timeslips/${slipId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
};

// Pinned Views API
export const pinnedViewsAPI = {
  list: async (board) => {
    const response = await apiClient.get(`${API_URL}/pinned-views?board=${board}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  create: async (board, name, state) => {
    const response = await apiClient.post(`${API_URL}/pinned-views`, { board, name, state }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  delete: async (id) => {
    const response = await apiClient.delete(`${API_URL}/pinned-views/${id}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  setDefault: async (id, isDefault) => {
    const response = await apiClient.post(`${API_URL}/pinned-views/${id}/default`, { is_default: isDefault }, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
};

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await apiClient.post(`${API_URL}/auth/login`, {
      email,
      password
    });
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },
  
  getUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAdmin: () => {
    const user = localStorage.getItem('user');
    if (!user) return false;
    return JSON.parse(user).is_admin === true;
  }
};


export const showFlashMessage = (text) => {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;z-index:99998;display:flex;align-items:center;justify-content:center;pointer-events:none;';
  const box = document.createElement('div');
  box.textContent = text;
  box.style.cssText = 'background:rgba(0,0,0,0.75);color:#fff;padding:16px 36px;border-radius:8px;font-size:15px;font-weight:500;opacity:0;transition:opacity 0.3s ease;';
  overlay.appendChild(box);
  document.body.appendChild(overlay);
  requestAnimationFrame(() => { box.style.opacity = '1'; });
  setTimeout(() => {
    box.style.opacity = '0';
    setTimeout(() => overlay.remove(), 300);
  }, 1500);
};

export const showErrorModal = (text) => {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;';

  const modal = document.createElement('div');
  modal.style.cssText = 'background:#fff;border-radius:8px;padding:32px 40px;text-align:center;box-shadow:0 4px 24px rgba(0,0,0,0.2);max-width:400px;width:90%;';

  const title = document.createElement('h3');
  title.textContent = 'Error';
  title.style.cssText = 'margin:0 0 12px;font-size:18px;font-weight:600;color:#DC3545;';

  const msg = document.createElement('p');
  msg.textContent = text;
  msg.style.cssText = 'margin:0 0 24px;font-size:14px;color:#666;';

  const btn = document.createElement('button');
  btn.textContent = 'OK';
  btn.style.cssText = 'background:#4ECFDB;color:#fff;border:none;padding:10px 40px;border-radius:4px;font-size:14px;cursor:pointer;font-weight:500;';
  btn.onmouseover = () => btn.style.background = '#3AB8C5';
  btn.onmouseout = () => btn.style.background = '#4ECFDB';
  btn.onclick = () => overlay.remove();

  modal.appendChild(title);
  modal.appendChild(msg);
  modal.appendChild(btn);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
};

export const showConfirmModal = (text, confirmLabel = 'Delete') => {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;';

    const modal = document.createElement('div');
    modal.style.cssText = 'background:#fff;border-radius:8px;padding:32px 40px;text-align:center;box-shadow:0 4px 24px rgba(0,0,0,0.2);max-width:400px;width:90%;';

    const msg = document.createElement('p');
    msg.textContent = text;
    msg.style.cssText = 'margin:0 0 24px;font-size:15px;color:#333;';

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:12px;justify-content:center;';

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.cssText = 'background:#e5e7eb;color:#333;border:none;padding:10px 32px;border-radius:4px;font-size:14px;cursor:pointer;font-weight:500;';
    cancelBtn.onclick = () => { overlay.remove(); resolve(false); };

    const confirmBtn = document.createElement('button');
    confirmBtn.textContent = confirmLabel;
    confirmBtn.style.cssText = 'background:#DC3545;color:#fff;border:none;padding:10px 32px;border-radius:4px;font-size:14px;cursor:pointer;font-weight:500;';
    confirmBtn.onmouseover = () => confirmBtn.style.background = '#C82333';
    confirmBtn.onmouseout = () => confirmBtn.style.background = '#DC3545';
    confirmBtn.onclick = () => { overlay.remove(); resolve(true); };

    btnRow.appendChild(cancelBtn);
    btnRow.appendChild(confirmBtn);
    modal.appendChild(msg);
    modal.appendChild(btnRow);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
  });
};
